import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CButton, CCard, CCardBody, CCardFooter, CCardHeader } from "@coreui/react-pro";
import { type UsableOfferDto } from "../../api/hooks/useOffers";
import { discountLabel, termsLine } from "../../lib/offer-copy";
import { routePaths } from "../../routes/route-paths";

interface OfferCardProps {
  offer: UsableOfferDto;
  /** The offers page passes h-100 so a row of them lines up. */
  className?: string;
}

/**
 * One offer the customer can use.
 *
 * Led by the discount and the wording staff wrote, not by the terms: "a month on us" is what makes
 * someone want it, and "100% for 1 month, max 1 strategy" is the same fact in a form nobody reads.
 * The terms go in the footer, where they answer the question the wording raises.
 */
export function OfferCard({ offer, className = "" }: OfferCardProps) {
  const { t } = useTranslation("offers");
  const navigate = useNavigate();

  return (
    <CCard className={className}>
      <CCardHeader>
        <div className="text-uppercase small fw-bold text-primary">{discountLabel(offer, t)}</div>
        <strong>{offer.publicName}</strong>
      </CCardHeader>
      <CCardBody className="d-flex flex-column">
        <p className="mb-3">{offer.customerWording}</p>
        <div className="mt-auto">
          <CButton color="primary" onClick={() => navigate(browseTo(offer))}>
            {offer.tradingStrategyIds.length === 1
              ? t("card.viewStrategy")
              : t("card.browseStrategies")}
          </CButton>
        </div>
      </CCardBody>
      <CCardFooter className="text-body-secondary small">{termsLine(offer, t)}</CCardFooter>
    </CCard>
  );
}

/**
 * An offer limited to one strategy goes straight to it; anything wider goes to the list. The cart is
 * where an offer is actually spent, so neither link pretends to apply it.
 */
function browseTo(offer: UsableOfferDto): string {
  return offer.tradingStrategyIds.length === 1
    ? routePaths.strategyDetails.replace(":id", offer.tradingStrategyIds[0])
    : routePaths.strategies;
}
